import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGift } from '@fortawesome/pro-light-svg-icons';

import Button from '@v2/shared/components/Button';
import TextButton from '@v2/shared/components/TextButton';
import { cn } from '@v2/shared/lib/utils';

type Props = {
  productName: string;
  onAdd: () => Promise<void> | void;
  onDismiss: () => void;
  className?: string;
};

/**
 * "Add one more as a gift", offered right after something goes in the cart.
 *
 * It lives in one place now: inside the cart, against the line that was just added. It
 * used to be drawn over the product tile on a desktop and only inside the cart on a
 * phone, which meant two copies of the offer, a rule about which viewport got which, and
 * a reason the cart could not open on a desktop — the drawer's backdrop landed on top of
 * the tile's copy and made it unclickable.
 *
 * Sized to sit between two cart lines in a 384px drawer, which is why the icon is inline
 * with the heading rather than stacked above it and the two answers share a row. It is
 * an interruption in a list, not a page of its own.
 *
 * Copy is the legacy lightbox's, deliberately — it has been converting on this page for
 * years and this is a framework port, not a rewrite of the offer.
 */
export default function GiftOffer({ productName, onAdd, onDismiss, className }: Props) {
  const [adding, setAdding] = useState(false);

  const add = async () => {
    setAdding(true);

    try {
      await onAdd();
    } finally {
      setAdding(false);
    }
  };

  return (
    <div
      className={cn('bg-primary-50 border-[1.5px] border-primary-600 rounded-lg p-3', className)}
      role="dialog"
      aria-label="Add one more as a gift"
    >
      <p className="flex items-center gap-2 font-semibold text-sm text-slate-900 m-0">
        <FontAwesomeIcon icon={faGift} className="text-primary-600 text-base" />
        Wait!
      </p>
      <p className="text-xs text-slate-600 mt-1 mb-0">
        Make a friend or relative happy by gifting <strong>{productName}</strong> for the same great price
        you&rsquo;re paying!
      </p>
      <div className="flex items-center gap-3 mt-2">
        <Button size="xs" onClick={add} isLoading={adding} loadingText="Adding...">
          Sure!
        </Button>
        <TextButton onClick={onDismiss} size="sm" color="secondary">
          No thanks
        </TextButton>
      </div>
    </div>
  );
}
